import React from 'react';
import LaCriteriaEditor from './LaCriteriaEditor';
import { MAX_GROUP_NAME as MAX_NAME } from '../../utils/lionAutoGroups';
import { effectiveFloor } from '../../utils/lionAutoFloors';
import { holdNeedsPrice } from '../../utils/lionAutoHold';
import { autoRunText, autoWarningText, pauseReasonText } from '../../utils/lionAutoRunner';
import { newGroupSaveHint } from '../../utils/lionAutoSave';
import { parseUtcDate, timeAgo } from '../../utils/dates';

// Create / edit one Lion Auto group: a name and its criteria. The group is
// its sections (picked on the map or a zone); our listings in them are its
// members by themselves. Their price-ladder order is clicked in the page's
// table (the Rank column), not here. The page previews the combined table
// live from the same draft. Save stores the group in our database (a new one
// with nothing ranked by hand comes back ranked in Smart sort order, which a
// line under the head tells everyone), then — with a floor (its own, the
// show's or the artist's) — the page prices it like Calculate prices (the
// ladder's new Bs are written to Skybox) and, for an admin, turns Auto on
// after that write (utils/lionAutoSave). Without a floor it only saves.
// rowOptions: the rows of ours and the market's in the draft's sections, for
// the Rows filter's dropdown (null while the listings load).
// initialAdvancedOpen: the advanced filters start open (a duplicate, whose
// filters are what tells it apart) instead of folded; read once per mount,
// and the page keys the editor per group.
// Auto (a saved group only; draft.autoEnabled, draft.savedAuto = the switch
// when the editor opened): the server re-ladders the group and writes its
// prices to Skybox unattended. For an admin (canEnableAuto) a group that is
// off opens ticked (autoSwitchDefault): saving turns Auto on, and unticking
// is how it stays off; a group the runner paused opens unticked. Auto needs
// a floor (inheritedFloor; null = not known yet); without one a ticked box
// on a group that is off just stays off. Anyone may switch a group on Auto
// off. Pricers see the switch only on a group already on Auto. savedGroup:
// the group as saved (who turned Auto on, the last run), null for a new
// group. autoRunner: the runner's { cadence, dryRun } when known (admins
// read its status), for the warning; null says it in general terms.
// saleRefill: this event's Reprice after a sale (its show's, else its
// artist's, else the runner's default) for every staff role, for the warning;
// null while unknown (the warning then leaves the after-sale run out).
// saveBlocked: why Save waits (the last save's group is still being priced
// and written), null when it doesn't.

function autoStateText(draft, savedGroup, floorOk) {
  const on = !!draft.autoEnabled;
  if (on && !draft.savedAuto) {
    return floorOk
      ? 'Turns on after the save prices the group. Untick to keep it off.'
      : 'Stays off: Auto needs a floor (set one here under Price ladder, for this show or for the artist).';
  }
  if (!on && draft.savedAuto) return 'Turns off when you save.';
  if (!on && savedGroup?.autoLastStatus === 'paused') {
    const why = pauseReasonText(savedGroup.autoLastSummary);
    return `Paused by the runner${why ? `: ${why}` : ''}. Tick it to turn it back on once the cause is fixed.`;
  }
  if (!on) return 'Off · stays off when you save; prices change when someone calculates the ladder.';
  const since = parseUtcDate(savedGroup?.autoEnabledAt);
  const by = savedGroup?.autoEnabledByName ? ` by ${savedGroup.autoEnabledByName}` : '';
  return `On${since ? ` since ${timeAgo(since)}` : ''}${by} · ${savedGroup ? autoRunText(savedGroup) : 'not run yet'}`;
}

function LaGroupEditor({
  draft, onChange, resolved, anchorListing, chartSections, rowOptions = null,
  onSave, onCancel, saving, saveError, notice, dirty, initialAdvancedOpen = false,
  canEnableAuto = false, savedGroup = null, autoRunner = null, saleRefill = null, inheritedFloor = null, saveBlocked = null,
}) {
  const name = draft.name || '';
  const nameOk = name.trim().length >= 1 && name.trim().length <= MAX_NAME;
  const hasSections = draft.criteria.sections.length > 0;

  // ---- Auto ----
  const isSaved = draft.id != null;
  const autoOn = !!draft.autoEnabled;
  // The floor the group would price with: its own, else the show's or the
  // artist's (unknown while the floors load: null)
  const floorsKnown = inheritedFloor !== null;
  const floorOk = effectiveFloor(draft.criteria.ladderFloorPct, inheritedFloor).pct > 0;
  const showAuto = isSaved && (canEnableAuto || !!draft.savedAuto);
  // On needs an admin (or a group already on Auto: switching it back is no
  // change) and a floor; off is always allowed
  const canTurnOn = (canEnableAuto || !!draft.savedAuto) && floorOk;
  let autoToggleTitle = 'Tick to turn Auto on after the save prices the group: the server then re-ladders it and writes its prices to Skybox on its own';
  if (autoOn) autoToggleTitle = draft.savedAuto ? 'Untick to stop Auto when you save' : 'Untick to keep Auto off when you save';
  const noFloorTitle = floorsKnown
    ? 'Set a floor (% of cost) first, here under Price ladder, for this show or for the artist: Auto never prices without one'
    : 'Reading the floors…';
  if (!autoOn && !floorOk) autoToggleTitle = noFloorTitle;
  else if (!autoOn && !canTurnOn) autoToggleTitle = 'Only an admin can turn Auto on';
  // The server refuses a save that leaves a group on Auto without a floor
  // (AUTO_NEEDS_FLOOR); a group that is off turns on only after the save,
  // and with no floor it just stays off. While the floors are unknown it is
  // the server's call.
  const autoNeedsFloor = autoOn && !!draft.savedAuto && floorsKnown && !floorOk;
  // A save of this saved group turns Auto on (ticked, off now, a floor): a
  // change to save even with nothing else edited, as ticking the box was
  const autoPending = isSaved && canEnableAuto && autoOn && !draft.savedAuto && floorOk;
  // Saving turns Auto on (a new group with a floor, or autoPending)
  const autoAfterSave = canEnableAuto && (!isSaved || autoPending);

  // The server refuses holding on with no hold price (CRITERIA_INVALID)
  const needsHoldPrice = holdNeedsPrice(draft.criteria);

  const canSave = nameOk && hasSections && !saving && !saveBlocked && (draft.id == null || dirty || autoPending) && !autoNeedsFloor && !needsHoldPrice;
  // A new group saved with nothing ranked by hand comes back ranked in Smart
  // sort order (the server); the line under the head says so to everyone
  const newHint = isSaved ? null : newGroupSaveHint({ ladderIds: draft.criteria.ladderIds, canEnableAuto });
  const smartFirst = isSaved ? '' : ' (with nothing ranked by hand, it is ranked in Smart sort order first)';
  let saveTitle = hasSections
    ? `Save the group${smartFirst}, then (with a floor) price its ranked listings through Skybox's what-if and write the prices to Skybox${autoAfterSave ? ', then turn Auto on' : ''}`
    : 'Pick at least one section on the map or a zone first';
  if (saveBlocked) saveTitle = saveBlocked;
  else if (autoNeedsFloor) saveTitle = 'Auto needs a floor: set one here under Price ladder, for this show or for the artist, or turn Auto off';
  else if (needsHoldPrice) saveTitle = 'Set a hold price, or turn holding off';

  return (
    <div className="la-panel la-editor">
      <div className="la-panel-head">
        <h3>{draft.id == null ? 'New group' : 'Edit group'}</h3>
        <input
          className={`form-input form-input-sm la-name-input${nameOk ? '' : ' la-input-bad'}`}
          value={name}
          maxLength={MAX_NAME}
          placeholder="Group name"
          onChange={(e) => onChange({ name: e.target.value })}
        />
        <span className="la-push-right" />
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={!canSave}
          onClick={onSave}
          title={saveTitle}
        >
          {saving ? 'Saving…' : 'Save group'}
        </button>
        <button type="button" className="btn btn-secondary btn-sm" disabled={saving} onClick={onCancel}>Cancel</button>
      </div>

      {notice && <div className="la-status warn">{notice}</div>}
      {saveError && saveError.length > 0 && (
        <div className="la-status error">
          {saveError.length === 1 ? saveError[0] : (
            <ul className="la-detail-list">{saveError.map((line, i) => <li key={i}>{line}</li>)}</ul>
          )}
        </div>
      )}

      {showAuto && (
        <div className={`la-auto${autoOn ? ' on' : ''}`}>
          <label className="la-check" title={autoToggleTitle}>
            <input
              type="checkbox"
              checked={autoOn}
              disabled={saving || (!autoOn && !canTurnOn)}
              onChange={(e) => onChange({ autoEnabled: e.target.checked })}
            />
            <strong>Auto</strong>
          </label>
          <span className="la-hint">{autoStateText(draft, savedGroup, floorOk)}</span>
          {!autoOn && !floorOk && floorsKnown && canEnableAuto && (
            <span className="la-hint warn">Needs a floor (% of cost): set one here under Price ladder, for this show or for the artist (Floors).</span>
          )}
        </div>
      )}
      {newHint && <div className="la-hint la-auto-new" title={newHint.title}>{newHint.text}</div>}
      {showAuto && autoOn && (draft.savedAuto || floorOk) && (
        <div className="la-status warn">
          {autoWarningText({ cadence: autoRunner?.cadence ?? null, dryRun: autoRunner?.dryRun ?? null, saleRefill })}
        </div>
      )}
      {autoNeedsFloor && (
        <div className="la-status error">
          Auto needs a floor: set one here under Price ladder, for this show or for the artist (Floors), or turn Auto off to clear it.
        </div>
      )}

      <LaCriteriaEditor
        criteria={draft.criteria}
        onChange={(criteria) => onChange({ criteria })}
        resolved={resolved}
        anchorListing={anchorListing}
        chartSections={chartSections}
        rowOptions={rowOptions}
        initialAdvancedOpen={initialAdvancedOpen}
        inheritedFloor={inheritedFloor}
      />
    </div>
  );
}

export default LaGroupEditor;
